import { markResolved } from './diceTrace.ts';
/**
 * RWBR — the EZRA PHOTO FINISH ATTEMPT (PFA).
 *
 * Source: `EZRA TRACK PACK.docx`, section *PHOTO FINISH ATTEMPT (PFA)*. Fully
 * printed — components, implementation, procedure and seven caveats — and it is
 * the rare add-on in this corpus that needs no ruling to build. Every sentence
 * below is quoted where it is implemented.
 *
 * ── WHY THIS ONE IS NOT BLOCKED BY THE UNDEFINED "DECIDER DIE" ─────────────
 *
 * `CROSS_REFERENCE.md` B4 records that the **decider die is never defined
 * anywhere in the corpus** — no face count, no read. It gates the pit-violation
 * speeding rows, it gates EZRA-S2's restart trigger, and it orders PFAs.
 *
 * For the PFA it does not matter, and that is a fact about the rule rather than
 * a convenience: the only thing read off it is **parity**. *"An even decider
 * roll means PFA's start at the back of the field. On an odd result they start
 * from the front."* Every die with an even number of faces splits 50/50 on
 * parity, so a d6 answers the question identically to a d10 or a d20. The
 * undefined face count is genuinely irrelevant here, and `decider.ts` does not
 * need to exist for this module to be correct.
 *
 * That is NOT true of EZRA-S2's *"positive decider die roll"* on the restart
 * trigger, where "positive" has no meaning without a definition. Same undefined
 * die, two very different consequences — worth stating so nobody assumes B4
 * blocks everything it touches.
 *
 * ── WHAT A "GAS PEDAL CHECK" IS ────────────────────────────────────────────
 *
 * Not invented: it is the base game's own challenge condition. RB/p.vii — *"the
 * challenging driver rolls within his 'gas pedal' range"* — a red die of `<=`
 * the driver's gas pedal. `challenge.ts` has read it that way since the first
 * week and this module reads it the same way, so a PFA feels like a pass
 * because it is resolved like one.
 *
 * ── THE ONE AMBIGUITY, AND WHY IT COSTS NOTHING ────────────────────────────
 *
 * AMBIGUITY: the sheet says *"**If** a chipped driver **successfully** executes
 * a PFA, his performance chip(s) are consumed"* — conditional on success — but
 * the two-chip caveat says *"A driver with 2 PC's performs the PFA with a -1
 * modifier … He consumes **both** PCs"*, which reads as consumed by attempting.
 *
 * Implemented as the explicit conditional sentence: **consumed on success.**
 * The two-chip sentence is answering *which* chips (both, not one), not *when*.
 * And the stakes are nil either way — a PFA happens after the last action of the
 * last turn, so a chip retained has nothing left to be spent on. Recorded
 * because a reading that costs nothing today can cost something the day
 * somebody scores chips at the flag.
 */

import type { Car, RaceState } from './types.ts';

import type { Dice } from './dice.ts';
import type { DiceRecord, Modifier, PositionDelta, RuleEvent } from './ruleEvent.ts';

export const CITE_PFA =
  'EZRA TRACK PACK.docx — "PHOTO FINISH ATTEMPT (PFA)". Last turn of race; Top Group, Leader and ' +
  'Middle Group; end of turn, all other actions completed; unused performance chips.';
export const CITE_PFA_PROCEDURE =
  'EZRA-TP PFA Procedure — "the action is implemented via gas pedal checks. The chipped driver ' +
  'executes first. If the attack fails, the PFA ends. If the chipped driver check succeeds, the ' +
  'targeted driver must successfully execute a gas pedal check to defend. If the defense succeeds, ' +
  'no positions are exchanged and the PFA is ended. If the defense fails, the chipped driver swaps ' +
  'positions with the targeted driver and advances one position in the standings."';
export const CITE_PFA_ORDER =
  'EZRA-TP PFA Caveats — "The order of multiple PFA\'s are directed by a decider die once for all ' +
  'possible PFA\'s. An even decider roll means PFA\'s start at the back of the field. On an odd ' +
  'result they start from the front."';
export const CITE_GAS_CHECK =
  'RB/p.vii — a gas pedal check is a red die equal to or under the driver\'s gas pedal, the base ' +
  'game\'s own challenge condition.';
export const CITE_PFA_FUEL =
  'EZRA-TP PFA Caveats — "A driver who has executed a skipped pit and runs out of gas is ineligible ' +
  'to execute a PFA and can be a PFA target. Any chips are considered forfeit for these PFA purposes."';

// ---------------------------------------------------------------------------
// The printed limits
// ---------------------------------------------------------------------------

/** "A chipped driver can only attempt one PFA per turn." */
export const MAX_ATTEMPTS_PER_DRIVER = 1;

/**
 * "A driver cannot have more than two PFAs executed against him. Once the limit
 * is reached, any other eligible PFA is void. (PFAs happen in an instant of
 * time.)"
 */
export const MAX_PFAS_AGAINST = 2;

/** "A driver with 2 PC's performs the PFA with a -1 modifier to his gas pedal check die roll." */
export const TWO_CHIP_BONUS = -1;
export const TWO_CHIP_THRESHOLD = 2;

/**
 * "Top Group and Leader and Middle Group" — positions 1 through 19. The BOTTOM
 * group is out entirely, on both sides of a PFA: RB/p.xv already forbids a
 * BOTTOM car from holding a chip, so it could not attack; and the components
 * list scopes the whole rule to the top nineteen, so it cannot be a target
 * either.
 */
export const LAST_ELIGIBLE_POSITION = 19;

export function inPfaZone(position: number): boolean {
  return position >= 1 && position <= LAST_ELIGIBLE_POSITION;
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export type PfaOutcome =
  /** The attacker's gas check missed. "If the attack fails, the PFA ends." */
  | 'attack-failed'
  /** The attack landed and the defender held. "no positions are exchanged". */
  | 'defended'
  /** The attack landed and the defence missed — the cars swap. */
  | 'passed';

export interface PfaAttempt extends RuleEvent {
  eventType: 'photo-finish';
  /**
   * `pfa.won-the-race` is its own tag rather than a flag on `pfa.passed`,
   * because significance and art selection are keyed on the TAG — and a pass on
   * the line for the win is not the same moment as a pass on the line for 14th.
   * One tag for both would have made them interchangeable to every layer above.
   */
  momentTag: 'pfa.attack-failed' | 'pfa.defended' | 'pfa.passed' | 'pfa.won-the-race';
  attackerId: string;
  defenderId: string;
  /** 1-based positions BEFORE the attempt. */
  fromPosition: number;
  toPosition: number;
  outcome: PfaOutcome;
  /** Chips the attacker held. 2+ buys the -1. */
  chipsHeld: number;
  chipsConsumed: number;
  attackRoll: number;
  /** The roll after the two-chip modifier — what was actually compared. */
  attackEffective: number;
  attackerGas: number;
  defenceRoll: number | null;
  defenderGas: number | null;
  /** True when the win took the race lead. "it is possible to win a race by a Photo Finish." */
  wonTheRace: boolean;
}

export interface PhotoFinishResult extends RuleEvent {
  eventType: 'photo-finish';
  momentTag: 'pfa.none' | 'pfa.round';
  /** The decider die, and which end of the field it started from. */
  decider: number | null;
  fromBack: boolean;
  attempts: PfaAttempt[];
  /** The running order after every attempt resolved. */
  order: Car[];
  /** True when the race lead changed hands on the line. */
  leadChanged: boolean;
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

const nameOf = (car: Car): string => car.card.name;

/**
 * Every chipped car that is directly behind an unchipped one, in board order.
 *
 * "any driver positioned in the top or middle groups in possession of an unused
 * performance chip and positioned directly behind a non-chipped driver may
 * attempt a Photo Finish Attempt."
 *
 * Recomputed between attempts rather than fixed up front, because a swap
 * changes who is behind whom — and the sheet's own caveat that a driver who
 * survived a failed PFA "can have a PFA directed against him when the chipped
 * driver's turn is called" only makes sense on a board that is still moving.
 */
function eligiblePairs(order: readonly Car[], lowFuel: ReadonlySet<string>): { attacker: Car; defender: Car }[] {
  const pairs: { attacker: Car; defender: Car }[] = [];
  for (let i = 1; i < order.length; i++) {
    const attacker = order[i];
    const defender = order[i - 1];
    const attackPos = i + 1;
    if (!inPfaZone(attackPos)) break; // the board is in order; nothing past 19 qualifies
    // "Any chips are considered forfeit for these PFA purposes" — a car out of
    // fuel cannot attack even holding chips.
    if (lowFuel.has(attacker.card.driverId)) continue;
    if (attacker.chips < 1) continue;
    // "positioned directly behind a NON-CHIPPED driver".
    if (defender.chips > 0) continue;
    if (attacker.status !== 'running' || defender.status !== 'running') continue;
    pairs.push({ attacker, defender });
  }
  return pairs;
}

export interface PhotoFinishInput {
  state: RaceState;
  dice: Dice;
}

/**
 * Run the photo finish, after the last action of the last turn.
 *
 * Returns the order unchanged and `momentTag: 'pfa.none'` when nobody qualifies,
 * which is the common case — it needs a chipped car directly behind an
 * unchipped one inside the top nineteen, at the flag.
 */
export function resolvePhotoFinish(input: PhotoFinishInput): PhotoFinishResult {
  const { dice } = input;
  const order: Car[] = input.state.grid.filter((c) => c.status === 'running').map((c) => ({ ...c }));
  const lowFuel = new Set(input.state.lowFuel);
  const leaderBefore = order[0]?.card.driverId ?? null;

  const attempts: PfaAttempt[] = [];
  const diceRecords: DiceRecord[] = [];
  const attempted = new Set<string>();
  const targeted = new Map<string, number>();

  if (eligiblePairs(order, lowFuel).length === 0) {
    return {
      eventType: 'photo-finish',
      momentTag: 'pfa.none',
      ruleCitation: CITE_PFA,
      actorIds: [],
      positionDeltas: [],
      dice: [],
      modifiers: [],
      description: 'No photo finish — nobody came to the line with a chip in hand and a clear car ahead.',
      decider: null,
      fromBack: false,
      attempts: [],
      order,
      leadChanged: false,
    };
  }

  // "The order of multiple PFA's are directed by a decider die ONCE for all
  // possible PFA's." One roll, before any of them, and only its parity is read
  // — see the header on why the undefined die does not matter here.
  const decider = dice.d6();
  const fromBack = decider % 2 === 0;
  diceRecords.push({
    kind: 'd6',
    dice: [decider],
    total: decider,
    label: 'PFA decider die',
    flags: { even: fromBack },
  });

  for (;;) {
    const pairs = eligiblePairs(order, lowFuel).filter(
      (p) =>
        !attempted.has(p.attacker.card.driverId) &&
        (targeted.get(p.defender.card.driverId) ?? 0) < MAX_PFAS_AGAINST,
    );
    if (pairs.length === 0) break;

    // Back of the field on an even decider, front on an odd one.
    const pair = fromBack ? pairs[pairs.length - 1] : pairs[0];
    const { attacker, defender } = pair;
    const from = order.indexOf(attacker) + 1;
    const to = order.indexOf(defender) + 1;

    attempted.add(attacker.card.driverId);
    targeted.set(defender.card.driverId, (targeted.get(defender.card.driverId) ?? 0) + 1);

    // "A driver with 2 PC's performs the PFA with a -1 modifier to his gas
    // pedal check die roll." Lower is better on a gas check, so -1 helps.
    const chipsHeld = attacker.chips;
    const bonus = chipsHeld >= TWO_CHIP_THRESHOLD ? TWO_CHIP_BONUS : 0;
    const attackRoll = dice.d6('red');
    const attackEffective = attackRoll + bonus;
    const attackerGas = attacker.card.gasPedal;
    const attackOk = attackEffective <= attackerGas;

    const modifiers: Modifier[] = [
      { name: 'attacker GAS PEDAL', value: attackerGas, detail: CITE_GAS_CHECK },
      { name: 'performance chips held', value: chipsHeld },
    ];
    if (bonus !== 0) {
      modifiers.push({
        name: 'two performance chips',
        value: bonus,
        detail: 'EZRA-TP — "A driver with 2 PC\'s performs the PFA with a -1 modifier to his gas pedal check die roll."',
      });
    }
    const rolls: DiceRecord[] = [
      {
        kind: 'd6',
        dice: [attackRoll],
        total: attackEffective,
        label: `PFA attack — ${nameOf(attacker)} v gas ${attackerGas}`,
      },
    ];

    let outcome: PfaOutcome;
    let defenceRoll: number | null = null;
    let defenderGas: number | null = null;
    let chipsConsumed = 0;
    const positionDeltas: PositionDelta[] = [];

    if (!attackOk) {
      // "If the attack fails, the PFA ends."
      outcome = 'attack-failed';
    } else {
      defenderGas = defender.card.gasPedal;
      defenceRoll = dice.d6('red');
      rolls.push({
        kind: 'd6',
        dice: [defenceRoll],
        total: defenceRoll,
        label: `PFA defence — ${nameOf(defender)} v gas ${defenderGas}`,
      });
      modifiers.push({ name: 'defender GAS PEDAL', value: defenderGas, detail: CITE_GAS_CHECK });
      const defenceOk = defenceRoll <= defenderGas;
      if (defenceOk) {
        // "If the defense succeeds, no positions are exchanged and the PFA is ended."
        outcome = 'defended';
      } else {
        outcome = 'passed';
        // "his performance chip(s) are consumed and removed from play" — on
        // success, per the sheet's own conditional. See the header.
        chipsConsumed = chipsHeld;
        const ai = order.indexOf(attacker);
        const di = order.indexOf(defender);
        order[ai] = { ...defender, position: from };
        // Every chip he held goes into the pass — trait chips included. They
        // are SPENT, which EZRA-S2 §4.0 has nothing to say about; what it
        // protects is a chip taken away by a demotion, not one cashed in.
        order[di] = { ...attacker, position: to, chips: 0, traitChips: undefined };
        positionDeltas.push(
          { driverId: attacker.card.driverId, from, to },
          { driverId: defender.card.driverId, from: to, to: from },
        );
      }
    }

    diceRecords.push(...rolls);
    // "The leader, if unchipped, can be challenged in a PFA. So it is possible
    // to win a race by a Photo Finish."
    const wonTheRace = outcome === 'passed' && to === 1;

    const attempt: PfaAttempt = {
      eventType: 'photo-finish',
      momentTag: wonTheRace ? 'pfa.won-the-race' : (`pfa.${outcome}` as PfaAttempt['momentTag']),
      ruleCitation: `${CITE_PFA}; ${CITE_PFA_PROCEDURE}`,
      actorIds: [attacker.card.driverId, defender.card.driverId],
      positionDeltas,
      dice: rolls,
      modifiers,
      description:
        outcome === 'attack-failed'
          ? `${nameOf(attacker)} throws it at ${nameOf(defender)} on the line and cannot make the run — ${attackEffective} against a gas pedal of ${attackerGas}.`
          : outcome === 'defended'
            ? `${nameOf(attacker)} gets alongside ${nameOf(defender)} at the flag and ${nameOf(defender)} holds him off by inches.`
            : wonTheRace
              ? `${nameOf(attacker)} takes it at the stripe — past ${nameOf(defender)} on the last yard for the win. A photo finish.`
              : `${nameOf(attacker)} nips ${nameOf(defender)} on the line for ${to}${to === 2 ? 'nd' : to === 3 ? 'rd' : 'th'}.`,
      attackerId: attacker.card.driverId,
      defenderId: defender.card.driverId,
      fromPosition: from,
      toPosition: to,
      outcome,
      chipsHeld,
      chipsConsumed,
      attackRoll,
      attackEffective,
      attackerGas,
      defenceRoll,
      defenderGas,
      wonTheRace,
    };
    markResolved([attempt], dice.rolls);
    attempts.push(attempt);
  }

  const leaderAfter = order[0]?.card.driverId ?? null;
  const leadChanged = leaderBefore !== leaderAfter;
  const passes = attempts.filter((a) => a.outcome === 'passed');

  return {
    eventType: 'photo-finish',
    momentTag: 'pfa.round',
    ruleCitation: `${CITE_PFA}; ${CITE_PFA_ORDER}`,
    actorIds: [...new Set(attempts.flatMap((a) => a.actorIds))],
    positionDeltas: attempts.flatMap((a) => a.positionDeltas),
    dice: diceRecords,
    modifiers: [
      { name: 'decider die', value: decider, detail: CITE_PFA_ORDER },
      { name: 'resolved from', value: fromBack ? 'the back of the field' : 'the front of the field' },
      { name: 'attempts', value: attempts.length },
      { name: 'passes completed', value: passes.length },
    ],
    description:
      attempts.length === 0
        ? 'No photo finish.'
        : `${attempts.length} car${attempts.length === 1 ? '' : 's'} threw one at the stripe, ` +
          `${passes.length} made it stick` +
          (leadChanged ? ' — and the race was won on the line.' : '.'),
    decider,
    fromBack,
    attempts,
    order,
    leadChanged,
  };
}
